import { AppNoticeCoachmark } from '@/components/app-notice-coachmark'
import { ChatPanel } from '@/components/chat/chat-panel'
import {
  collectVisibleWelcomeBackItems,
  useWelcomeBackMenuFtue,
} from '@/components/layout/use-welcome-back-menu-ftue'
import { LearningPanel } from '@/components/learning-panel'
import { ResumePreviewPanel } from '@/components/resume-preview-panel'
import { type ParticipantRouteAccessId, canAccessParticipantRoute } from '@/config/route-access'
import { FtueHost, useFtueAnchor, useFtueServerSync, useFtueSseBridge, useFtueStore } from '@/ftue'
import { useAccessControl } from '@/hooks/use-access-control'
import { useAuth } from '@/hooks/use-auth'
import { useDocumentProcessingProgress } from '@/hooks/use-document-processing-progress'
import {
  isResumeScanAgentActive,
  isResumeScanAgentCompleted,
  isResumeScanAgentFailed,
  useDocuments,
} from '@/hooks/use-documents'
import { subscribe, useEventStream, useSSEEvent } from '@/hooks/use-event-stream'
import { useMyJobMatches } from '@/hooks/use-external-jobs'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useDeleteJobSearch, useJobSearches } from '@/hooks/use-job-searches'
import { learningKeys } from '@/hooks/use-learning'
import { useNavVisibility } from '@/hooks/use-nav-visibility'
import { useParticipantWorkflowRefresh } from '@/hooks/use-participant-workflow-refresh'
import {
  enableAiInterview,
  enableCoachRoster,
  enableCoaching,
  enableLearn,
  enableSkillsMap,
} from '@/lib/feature-flags'
import { groupMatchesByDocumentId } from '@/lib/group-matches-by-document-id'
import {
  isPendingChatGeneratedTopic,
  loadFirstModuleForTopic,
  loadNextModuleForTopic,
} from '@/lib/learning-chat-panel'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { learningService } from '@/services/learning.service'
import { useChatStore } from '@/stores/chat-store'
import { useSeveranceElectionStore } from '@/stores/severance-election-store'
import { useUploadProgressStore } from '@/stores/upload-progress-store'
import { type Document, type ExternalJobMatchWithJob, getUserDisplayName } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  Briefcase,
  Building2,
  ChevronDown,
  FileText,
  GraduationCap,
  Home as HomeIcon,
  Kanban,
  Landmark,
  Library,
  LifeBuoy,
  Loader2,
  LogOut,
  Map as MapIcon,
  Menu,
  MessageCircle,
  MessagesSquare,
  Mic,
  type Sparkles,
  Trash2,
  User,
  X,
} from 'lucide-react'
import {
  type CSSProperties,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

const MATCHED_RESUME_IDS_STORAGE_KEY = 'opportunities.matchedResumeIds'
const VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY = 'opportunities.viewedReadyMatchedResumeIds'
// Profile stays in the account menu rather than becoming a primary journey
// destination. The menu itself lives at the bottom of the desktop sidebar.
const PROFILE_IN_USER_MENU = true

function readMatchedResumeIdsFromStorage(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(MATCHED_RESUME_IDS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((value) => Number(value))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => b - a)
  } catch {
    return []
  }
}

function readViewedReadyMatchedResumeIdsFromStorage(): number[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.map((value) => Number(value)).filter((id) => Number.isFinite(id))
  } catch {
    return []
  }
}

/** Full match count for a resume once job matching has finished (avoids biased partial counts from globally paginated /me matches). */
function sidebarMatchedJobCount(
  resume: Document | undefined,
  matchesForResume: ExternalJobMatchWithJob[] | undefined,
): number {
  // Keep sidebar count aligned with Opportunities list, which is driven by live match rows.
  if (matchesForResume && matchesForResume.length > 0) {
    return matchesForResume.length
  }
  return resume?.job_matching_total ?? 0
}

interface NavItem {
  name: string
  href: string
  icon: typeof Sparkles
  matchPaths: string[]
  routeId: ParticipantRouteAccessId
  badgeKey?: 'resume'
  hidden?: boolean
  locked?: boolean
  lockHint?: string
  /**
   * Heading this destination sits under in the expanded sidebar. Omitted for an
   * ungrouped entry pinned above the groups (Home); the compact icon rail ignores
   * it entirely.
   */
  section?: 'Prepare' | 'Find & Apply' | 'Learn & Get Support' | 'Plan Your Transition'
  ftue?: { id: string; body: string }
  children?: NavItem[]
}

/**
 * The compact rail is for the two agent workspaces, which need the width.
 * Benefits is an ordinary page and keeps the full sidebar.
 */
export function usesCompactParticipantSidebar(pathname: string): boolean {
  return ['/resume-builder', '/agent'].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  )
}

// Sidebar order for the expanded nav. Home is deliberately absent: it carries no
// `section`, so it sorts ahead of every grouped destination and renders without a
// heading (see `visibleCoachChildren` / `showSection`).
const NAV_DESTINATION_ORDER = [
  'Resumes',
  'Career Map',
  'Practice Interview',
  'Jobs Board',
  'Job Agent',
  'Application Tracker',
  'Learn',
  'Resource Library',
  'Coaching',
  'Benefits',
  'Start a new business',
  'Retirement',
] as const

export function AppLayout() {
  // Start SSE connection for real-time status updates
  useEventStream()
  const queryClient = useQueryClient()

  // Bridge job-matching SSE events onto FTUE engine signals (PEL-515).
  useFtueSseBridge()
  // Hydrate FTUE state from the user (/me) and persist mark-seen server-side (PEL-515).
  useFtueServerSync()
  // Anchor for the FTUE Screen 3 handoff coachmark, targeting the live sidebar
  // scan item for the resume being matched (consumed by PEL-519). Attached to that
  // item (generating + ready states) only on the Resume route + desktop (see the
  // `ref=` sites) so off-page/mobile the anchor is absent.
  const scanAgentsAnchorRef = useFtueAnchor('sidebar-scan-agents')
  const isMobile = useIsMobile()

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const authorization = useAccessControl()
  useParticipantWorkflowRefresh(
    authorization.data?.participant?.cooling_off_ends_at,
    authorization.refetch,
  )
  const urlSearch = new URLSearchParams(location.search)
  const resumeIdFromUrl = urlSearch.get('resume_id') ? Number(urlSearch.get('resume_id')) : null
  const jobSearchIdFromUrl = urlSearch.get('job_search_id')
    ? Number(urlSearch.get('job_search_id'))
    : null
  const isOpportunitiesRoute =
    location.pathname === '/opportunities' || location.pathname.startsWith('/opportunities/')
  const isAgentRoute = location.pathname === '/agent' || location.pathname.startsWith('/agent/')
  const isResumeBuilderRoute =
    location.pathname === '/resume-builder' || location.pathname.startsWith('/resume-builder/')
  const compactSidebar = usesCompactParticipantSidebar(location.pathname)
  // The guided severance election is full-screen until the agreement is signed;
  // the page owns when that ends (see severance-election-store).
  const electionNavHidden = useSeveranceElectionStore((state) => state.navHidden)
  const hideNav = location.pathname === '/benefits/election' && electionNavHidden
  // The coach is on by default. It is hidden on the two agent workspaces,
  // which are themselves chat surfaces -- a second chat panel beside them is a
  // competing entry point for the same conversation -- and on the tracker,
  // whose table needs the full width of the pane. It is also hidden while the
  // severance election gate holds the platform locked (`workflow_restricted`),
  // and returns once the election is finalized and access is released.
  const isTrackerRoute =
    location.pathname === '/tracker' || location.pathname.startsWith('/tracker/')
  const isSeveranceGateLocked = authorization.data?.participant?.workflow_restricted === true
  // The coach is currently hidden on every screen. The per-route conditions
  // are kept so the panel can be restored by dropping the leading `true`.
  const hideCoachPanel =
    true || isAgentRoute || isResumeBuilderRoute || isTrackerRoute || isSeveranceGateLocked
  const isResumeRoute = location.pathname === '/resume' || location.pathname.startsWith('/resume/')
  const isLearnRoute = location.pathname === '/learn' || location.pathname.startsWith('/learn/')
  const [hasMounted, setHasMounted] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileChatOpen, setMobileChatOpen] = useState(false)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false)
  const [dismissedCompactSubmenu, setDismissedCompactSubmenu] = useState<string | null>(null)
  const coachAttentionActive = useChatStore((state) => state.coachAttentionActive)
  const clearCoachAttention = useChatStore((state) => state.clearCoachAttention)
  const mobileCoachOpenToken = useChatStore((state) => state.mobileCoachOpenToken)
  const handledMobileCoachOpenRef = useRef(0)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  // Open coach overlay from Opportunities on mobile (no side panel there).
  useEffect(() => {
    if (isAgentRoute) return
    if (mobileCoachOpenToken === 0 || mobileCoachOpenToken === handledMobileCoachOpenRef.current) {
      return
    }
    handledMobileCoachOpenRef.current = mobileCoachOpenToken
    setMobileChatOpen(true)
  }, [mobileCoachOpenToken, isAgentRoute])
  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  // Close user menu when clicking outside
  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  // Close mobile menu on route change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally trigger on pathname change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen || mobileChatOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen, mobileChatOpen])

  // Resizable chat panel
  const MIN_CHAT_WIDTH = 300
  const MAX_CHAT_WIDTH = 700
  const DEFAULT_CHAT_WIDTH = 420
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH)
  const isResizingRef = useRef(false)

  const handleResizeStart = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      isResizingRef.current = true
      const startX = e.clientX
      const startWidth = chatWidth

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const delta = startX - moveEvent.clientX // dragging left = wider
        const newWidth = Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, startWidth + delta))
        setChatWidth(newWidth)
      }

      const handleMouseUp = () => {
        isResizingRef.current = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [chatWidth],
  )
  const { hasResume, documents, isResumeStatusResolved } = useNavVisibility()
  const canUseParticipantRoute = useCallback(
    (routeId: ParticipantRouteAccessId) =>
      Boolean(authorization.data && canAccessParticipantRoute(authorization.data, routeId)),
    [authorization.data],
  )
  const canUseElection = canUseParticipantRoute('participant.election')
  const isWorkflowRestricted = authorization.data?.participant?.workflow_restricted === true
  const [navigationFtueReady, setNavigationFtueReady] = useState(false)

  // The sidebar changes when the document query resolves. Wait until the complete
  // navigation has survived two layout frames before allowing an anchored tour to
  // start, so its targets cannot disappear midway through dashboard hydration.
  useEffect(() => {
    if (!hasMounted || !isResumeStatusResolved) {
      setNavigationFtueReady(false)
      return
    }

    let firstFrame = 0
    let secondFrame = 0
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => setNavigationFtueReady(true))
    })

    return () => {
      window.cancelAnimationFrame(firstFrame)
      window.cancelAnimationFrame(secondFrame)
    }
  }, [hasMounted, isResumeStatusResolved])
  const { isSuccess: documentsQuerySuccess } = useDocuments()
  const { data: myMatches, isSuccess: matchesQuerySuccess } = useMyJobMatches(0, 100)
  const deleteJobSearch = useDeleteJobSearch()
  const { data: jobSearchesData } = useJobSearches(0, 20)
  const customSearchSets = useMemo(
    () => (jobSearchesData?.items ?? []).filter((search) => search.source_type === 'custom_search'),
    [jobSearchesData],
  )
  const activeCustomSearchResumeIdSet = useMemo(() => {
    const resumeIds = customSearchSets
      .filter((search) => search.status === 'pending' || search.status === 'matching')
      .map((search) => search.document_id)
      .filter((resumeId) => Number.isFinite(resumeId))
    return new Set(resumeIds)
  }, [customSearchSets])
  const matchedJobsByResume = useMemo(
    () => groupMatchesByDocumentId(myMatches?.items),
    [myMatches?.items],
  )
  const matchedResumeIds = useMemo(() => {
    return Object.keys(matchedJobsByResume)
      .map((k) => Number(k))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => b - a)
  }, [matchedJobsByResume])
  const [persistedMatchedResumeIds, setPersistedMatchedResumeIds] = useState<number[]>(
    readMatchedResumeIdsFromStorage,
  )
  const [viewedReadyMatchedResumeIds, setViewedReadyMatchedResumeIds] = useState<number[]>(
    readViewedReadyMatchedResumeIdsFromStorage,
  )

  // Keep persisted ids in sync with fetched matches and explicit resume_id links.
  useEffect(() => {
    const merged = Array.from(
      new Set([
        ...persistedMatchedResumeIds,
        ...matchedResumeIds,
        ...(resumeIdFromUrl !== null ? [resumeIdFromUrl] : []),
      ]),
    ).sort((a, b) => b - a)

    const unchanged =
      merged.length === persistedMatchedResumeIds.length &&
      merged.every((id, idx) => id === persistedMatchedResumeIds[idx])
    if (unchanged) return

    setPersistedMatchedResumeIds(merged)
    try {
      window.localStorage.setItem(MATCHED_RESUME_IDS_STORAGE_KEY, JSON.stringify(merged))
    } catch {
      // Ignore storage write issues.
    }
  }, [matchedResumeIds, resumeIdFromUrl, persistedMatchedResumeIds])

  // If the user is viewing a specific resume's matched jobs via `resume_id`,
  // ensure that resume appears in the sidebar even if the match grouping
  // hasn't populated the full list yet.
  const effectiveMatchedResumeIds = useMemo(() => {
    return persistedMatchedResumeIds
  }, [persistedMatchedResumeIds])
  const isVisibleResume = useCallback(
    (doc: Document) =>
      doc.document_type === 'resume' &&
      doc.status !== 'failed' &&
      doc.status !== 'invalid_document',
    [],
  )
  const resumeById = useMemo(() => {
    const map: Record<number, Document> = {}
    for (const d of documents ?? []) {
      if (!isVisibleResume(d)) continue
      map[d.id] = d
    }
    return map
  }, [documents, isVisibleResume])
  const readyToBlinkResumeIds = useMemo(() => {
    return effectiveMatchedResumeIds.filter((resumeId) => {
      const resume = resumeById[resumeId]
      const isReady = resume ? isResumeScanAgentCompleted(resume) : false
      return isReady && !viewedReadyMatchedResumeIds.includes(resumeId)
    })
  }, [effectiveMatchedResumeIds, resumeById, viewedReadyMatchedResumeIds])
  const generatingMatchResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter(
        (doc) =>
          isVisibleResume(doc) &&
          !activeCustomSearchResumeIdSet.has(doc.id) &&
          isResumeScanAgentActive(doc),
      )
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [activeCustomSearchResumeIdSet, documents, isVisibleResume])
  const failedMatchResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter(
        (doc) =>
          isVisibleResume(doc) &&
          !activeCustomSearchResumeIdSet.has(doc.id) &&
          isResumeScanAgentFailed(doc),
      )
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [activeCustomSearchResumeIdSet, documents, isVisibleResume])
  const generatingMatchResumeIdSet = useMemo(
    () => new Set(generatingMatchResumeIds),
    [generatingMatchResumeIds],
  )
  const completedResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter((doc) => isVisibleResume(doc) && isResumeScanAgentCompleted(doc))
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [documents, isVisibleResume])
  const effectiveOpportunityResumeIds = useMemo(() => {
    return Array.from(new Set([...effectiveMatchedResumeIds, ...completedResumeIds])).sort(
      (a, b) => b - a,
    )
  }, [effectiveMatchedResumeIds, completedResumeIds])
  const readyMatchedResumeIds = useMemo(() => {
    return effectiveOpportunityResumeIds.filter((resumeId) => {
      if (generatingMatchResumeIdSet.has(resumeId)) return false
      const resume = resumeById[resumeId]
      const hasLiveMatches = (matchedJobsByResume[resumeId]?.length ?? 0) > 0
      const isCompleted = resume ? isResumeScanAgentCompleted(resume) : false
      return isCompleted || hasLiveMatches
    })
  }, [effectiveOpportunityResumeIds, generatingMatchResumeIdSet, matchedJobsByResume, resumeById])
  // PEL-932: legacy scan result URLs remain compatible, but Jobs no longer
  // exposes job-search/resume-scan agents as standalone sidebar entries.
  const hasOpportunitySubsections = false

  // Drop "viewed" ids only once documents and matches are loaded; otherwise activeReadyIds is
  // empty and would incorrectly clear localStorage (sidebar blink on every login).
  useEffect(() => {
    if (!documentsQuerySuccess || !matchesQuerySuccess) return

    const activeReadyIds = effectiveMatchedResumeIds.filter((resumeId) => {
      if (generatingMatchResumeIdSet.has(resumeId)) return false
      const resume = resumeById[resumeId]
      return resume ? isResumeScanAgentCompleted(resume) : false
    })
    const nextViewed = viewedReadyMatchedResumeIds.filter((id) => activeReadyIds.includes(id))
    if (nextViewed.length === viewedReadyMatchedResumeIds.length) return
    setViewedReadyMatchedResumeIds(nextViewed)
    try {
      window.localStorage.setItem(
        VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY,
        JSON.stringify(nextViewed),
      )
    } catch {
      // Ignore storage write issues.
    }
  }, [
    documentsQuerySuccess,
    matchesQuerySuccess,
    effectiveMatchedResumeIds,
    generatingMatchResumeIdSet,
    resumeById,
    viewedReadyMatchedResumeIds,
  ])

  const markMatchedResumeViewed = useCallback((resumeId: number) => {
    setViewedReadyMatchedResumeIds((prev) => {
      if (prev.includes(resumeId)) return prev
      const next = [...prev, resumeId]
      try {
        window.localStorage.setItem(
          VIEWED_READY_MATCHED_RESUME_IDS_STORAGE_KEY,
          JSON.stringify(next),
        )
      } catch {
        // Ignore storage write issues.
      }
      return next
    })
  }, [])

  const handleDeleteJobSearch = useCallback(
    async (searchId: number, searchTitle?: string | null) => {
      const label = searchTitle?.trim() || `Search ${searchId}`
      const confirmed = window.confirm(`Delete "${label}"?`)
      if (!confirmed) return
      try {
        await deleteJobSearch.mutateAsync(searchId)
        if (isOpportunitiesRoute && jobSearchIdFromUrl === searchId) {
          navigate('/opportunities')
        }
      } catch (error) {
        console.error('Failed to delete job search:', error)
      }
    },
    [deleteJobSearch, isOpportunitiesRoute, jobSearchIdFromUrl, navigate],
  )

  useEffect(() => {
    if (resumeIdFromUrl === null) return
    markMatchedResumeViewed(resumeIdFromUrl)
  }, [resumeIdFromUrl, markMatchedResumeViewed])
  const resumeTitleById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const d of documents ?? []) {
      if (d.document_type !== 'resume') continue
      map[d.id] = d.original_filename
    }
    return map
  }, [documents])
  const { processingItems } = useDocumentProcessingProgress(documents)
  const {
    isUploading: isUploadInProgress,
    uploadProgress,
    statusText: uploadStatusText,
  } = useUploadProgressStore()

  const displayName = getUserDisplayName(user)
  const displayInitial = displayName[0]?.toUpperCase() || 'U'

  const coachChildren: NavItem[] = useMemo(
    () => [
      {
        name: 'Home',
        href: '/home',
        icon: HomeIcon,
        matchPaths: ['/home'],
        routeId: 'participant.home',
        hidden: !canUseParticipantRoute('participant.home'),
        ftue: {
          id: 'home',
          body: 'Home gathers your resume, job matches, applications, and sessions in one place.',
        },
      },
      {
        name: 'Resumes',
        href: '/resume',
        icon: FileText,
        matchPaths: ['/resume'],
        routeId: 'participant.external-jobs',
        section: 'Prepare',
        badgeKey: 'resume' as const,
        hidden: !canUseParticipantRoute('participant.external-jobs'),
        ftue: {
          id: 'resume',
          body: 'Resumes is where you can upload, build, review, and manage every version of your resume.',
        },
        children: [
          {
            name: 'Build a resume',
            href: '/resume-builder',
            icon: FileText,
            matchPaths: ['/resume-builder'],
            routeId: 'participant.jobs',
            section: 'Prepare',
            hidden: !canUseParticipantRoute('participant.jobs'),
            ftue: {
              id: 'resume-builder',
              body: 'Build a resume with guided editing and live feedback.',
            },
          },
        ],
      },
      {
        name: 'Career Map',
        href: '/career-map',
        icon: MapIcon,
        matchPaths: ['/career-map', '/skills-map'],
        routeId: 'participant.skills-map',
        section: 'Prepare',
        hidden: !enableSkillsMap || !canUseParticipantRoute('participant.skills-map'),
        ftue: {
          id: 'skills-map',
          body: 'Career Map shows your strengths, adjacent skills, and areas you can develop next.',
        },
      },
      {
        name: 'Practice Interview',
        href: '/interview',
        icon: Mic,
        matchPaths: ['/interview'],
        routeId: 'participant.jobs',
        section: 'Prepare',
        hidden: !hasResume || !enableAiInterview || !canUseParticipantRoute('participant.jobs'),
        locked: !hasResume && canUseParticipantRoute('participant.jobs'),
        ftue: {
          id: 'practice-interview',
          body: 'Practice Interview gives you a place to rehearse answers and build confidence before interviews.',
        },
      },
      {
        name: 'Jobs Board',
        href: '/opportunities',
        icon: Briefcase,
        matchPaths: ['/opportunities'],
        routeId: 'participant.jobs',
        section: 'Find & Apply',
        hidden: !canUseParticipantRoute('participant.jobs'),
        ftue: {
          id: 'jobs',
          body: 'Jobs Board keeps your searches and matches together so you can return to promising opportunities.',
        },
      },
      {
        name: 'Job Agent',
        href: '/agent',
        icon: Bot,
        matchPaths: ['/agent'],
        routeId: 'participant.jobs',
        section: 'Find & Apply',
        hidden: !canUseParticipantRoute('participant.jobs'),
        ftue: {
          id: 'job-agent',
          body: 'Job Agent builds your queue and guides each application.',
        },
      },
      {
        name: 'Application Tracker',
        href: '/tracker',
        icon: Kanban,
        matchPaths: ['/tracker'],
        routeId: 'participant.external-jobs',
        section: 'Find & Apply',
        hidden: !canUseParticipantRoute('participant.external-jobs'),
        ftue: {
          id: 'application-tracker',
          body: 'Application Tracker keeps the roles you are pursuing organized by status.',
        },
      },
      {
        name: 'Learn',
        href: '/learn',
        icon: GraduationCap,
        matchPaths: ['/learn'],
        routeId: 'participant.learn',
        section: 'Learn & Get Support',
        hidden: !enableLearn || !canUseParticipantRoute('participant.learn'),
        ftue: {
          id: 'learn',
          body: 'Learn helps you build the skills that matter for your next move.',
        },
      },
      {
        name: 'Resource Library',
        href: '/resource-library',
        icon: Library,
        matchPaths: ['/resource-library'],
        routeId: 'participant.resource-library',
        section: 'Learn & Get Support',
        hidden: !canUseParticipantRoute('participant.resource-library'),
      },
      {
        name: 'Coaching',
        href: '/coaching',
        icon: MessagesSquare,
        matchPaths: ['/coaching'],
        routeId: 'participant.coaching',
        section: 'Learn & Get Support',
        hidden: !enableCoaching || !canUseParticipantRoute('participant.coaching'),
        ftue: {
          id: 'coaching',
          body: 'Coaching is where you can find support and manage your coaching sessions.',
        },
      },
      {
        name: 'Start a new business',
        href: '/start-a-business',
        icon: Building2,
        matchPaths: ['/start-a-business'],
        routeId: 'participant.start-business',
        section: 'Plan Your Transition',
        hidden:
          !enableCoaching ||
          !enableCoachRoster ||
          !canUseParticipantRoute('participant.start-business'),
        ftue: {
          id: 'start-business',
          body: 'Start a new business connects you with guidance for exploring and launching a business.',
        },
      },
      {
        name: 'Retirement',
        href: '/retirement',
        icon: Landmark,
        matchPaths: ['/retirement'],
        routeId: 'participant.retirement',
        section: 'Plan Your Transition',
        hidden:
          !enableCoaching ||
          !enableCoachRoster ||
          !canUseParticipantRoute('participant.retirement'),
        ftue: {
          id: 'retirement',
          body: 'Retirement provides guidance and coaching for planning your next chapter.',
        },
      },
      {
        // Consolidated 2026-09-02: this used to expand into two sub-items
        // ("Benefits" -> the old static support pages, "Exit Guide" -> the
        // real RAG-backed pages) -- collapsed into one flat "Benefits" menu
        // item pointing straight at Exit Guide's content, since Exit Guide
        // is the real feature going forward. Exit Guide's pages moved onto
        // the /benefits path itself (was /exit-guide); the old static pages
        // moved to /benefits-legacy -- untouched in code (kept as
        // reference, not deleted, not linked from the sidebar).
        name: 'Benefits',
        href: isWorkflowRestricted ? '/benefits/election' : '/benefits',
        icon: LifeBuoy,
        matchPaths: ['/benefits'],
        routeId: isWorkflowRestricted ? 'participant.election' : 'participant.benefits',
        section: 'Plan Your Transition',
        hidden:
          (!isWorkflowRestricted && !canUseParticipantRoute('participant.benefits')) ||
          (isWorkflowRestricted && !canUseElection),
        ftue: {
          id: 'benefits',
          body: 'Benefits gives you personalized guidance for understanding and choosing your available benefits.',
        },
      },
    ],
    [hasResume, canUseParticipantRoute, canUseElection, isWorkflowRestricted],
  )

  const welcomeBackMenuItems = useMemo(
    () =>
      collectVisibleWelcomeBackItems(
        coachChildren.filter((item) => !(PROFILE_IN_USER_MENU && item.href === '/profile')),
      ),
    [coachChildren],
  )
  const welcomeBackMenuFtue = useWelcomeBackMenuFtue({
    enabled: false,
    loginCount: user?.ftue_state?.loginCount ?? 0,
    ready: navigationFtueReady,
    items: welcomeBackMenuItems,
    isMobile,
    mobileMenuOpen,
  })

  const visibleCoachChildren = useMemo(
    () =>
      coachChildren
        .filter((item) => !item.hidden)
        .sort(
          (a, b) =>
            NAV_DESTINATION_ORDER.indexOf(a.name as (typeof NAV_DESTINATION_ORDER)[number]) -
            NAV_DESTINATION_ORDER.indexOf(b.name as (typeof NAV_DESTINATION_ORDER)[number]),
        ),
    [coachChildren],
  )

  // Flat list of all nav items for route guarding
  const allNavItems = coachChildren.flatMap((item) => [item, ...(item.children ?? [])])

  const {
    learningPanelOpen,
    learningContent,
    isLoadingLearning,
    learningError,
    learningTopicGenerating,
    previewPanelOpen,
    resumeContent,
    originalResumeContent,
    selectedTemplate,
    userDocumentId,
    isGeneratingRevision,
    isUpdatingPreview,
    previewJobTitle,
    previewCompany,
    previewPdfUrl,
    previewIsAgentTailored,
    previewLoading,
    setLearningPanelOpen,
    setLearningContent,
    setIsLoadingLearning,
    setLearningError,
    setLearningTopicGenerating,
    setPreviewPanelOpen,
    setSelectedTemplate,
    setIsGeneratingRevision,
    clearResumePreviewContext,
  } = useChatStore()

  // Redirect away from hidden/locked routes
  const isOnLockedRoute = allNavItems.some(
    (item) =>
      item.locked &&
      item.matchPaths.some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
      ),
  )

  // A route can appear "locked" simply because the resume status hasn't loaded
  // yet (e.g. direct navigation/refresh to /opportunities). In that window we
  // must not redirect, otherwise we bounce the user to /resume and drop their
  // deep-link params. Defer the decision until resume status is resolved and
  // show a loading shell in the meantime.
  const isResumeStatusPendingOnLockedRoute = isOnLockedRoute && !isResumeStatusResolved
  const shouldRedirectFromLockedRoute = isOnLockedRoute && isResumeStatusResolved

  // Navigate to the resume page when the preview *opens* from another page.
  // Only redirect on the transition into "open" — not on every route change
  // while it stays open, which would bounce the user back to /resume the moment
  // they navigate away (e.g. tapping Opportunities) and effectively lock nav.
  const prevPreviewPanelOpenRef = useRef(previewPanelOpen)
  useEffect(() => {
    const justOpened = !prevPreviewPanelOpenRef.current && previewPanelOpen
    prevPreviewPanelOpenRef.current = previewPanelOpen
    if (justOpened && !isResumeRoute && userDocumentId !== null) {
      navigate('/resume')
    }
  }, [previewPanelOpen, isResumeRoute, userDocumentId, navigate])

  // Navigate to Learn page when learning panel opens from another page
  useEffect(() => {
    if (enableLearn && learningPanelOpen && !isLearnRoute) {
      navigate('/learn')
    }
  }, [learningPanelOpen, isLearnRoute, navigate])

  const loadGeneratedTopicIntoPanel = useCallback(
    async (topicId: number) => {
      setIsLoadingLearning(true)
      setLearningError(null)
      try {
        const content = await loadFirstModuleForTopic(topicId)
        if (!content?.content) {
          setLearningError(
            'Your learning path was created but the first module is not ready yet. Open it from the Learn page.',
          )
          setLearningTopicGenerating(null)
          return
        }
        setLearningContent(content)
        setLearningTopicGenerating(null)
        queryClient.invalidateQueries({ queryKey: learningKeys.topics() })
        queryClient.invalidateQueries({ queryKey: learningKeys.topicDetail(topicId) })
        queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
      } catch (error) {
        console.error('Failed to load generated learning module:', error)
        setLearningError(
          'Failed to load your new learning module. Please try again from the Learn page.',
        )
        setLearningTopicGenerating(null)
      } finally {
        setIsLoadingLearning(false)
      }
    },
    [
      queryClient,
      setIsLoadingLearning,
      setLearningContent,
      setLearningError,
      setLearningTopicGenerating,
    ],
  )

  // Auto-load first module when a chat-queued topic finishes generating
  useSSEEvent('learning_topic_status_changed', (event) => {
    const data = event.data as { topic_id?: number; status?: string; status_text?: string }
    if (data.topic_id == null) return

    const state = useChatStore.getState()
    if (!state.learningPanelOpen) return
    if (
      !isPendingChatGeneratedTopic(data.topic_id, {
        learningTopicGenerating: state.learningTopicGenerating,
        learningContent: state.learningContent,
      })
    ) {
      return
    }

    if (data.status === 'failed') {
      setLearningTopicGenerating(null)
      setIsLoadingLearning(false)
      setLearningError(
        data.status_text ||
          'Failed to create your learning path. Please try again from the Learn page.',
      )
      return
    }

    if (data.status !== 'completed') return

    void loadGeneratedTopicIntoPanel(data.topic_id)
  })

  // Side panel logic: learning takes priority
  const showLearningPanel = learningPanelOpen
  const showPreviewPanel =
    isResumeRoute && userDocumentId !== null && previewPanelOpen && !showLearningPanel

  // Handle generate PDF from preview panel
  const handleGenerateFromPreview = async () => {
    if (!userDocumentId || previewIsAgentTailored) return

    setIsGeneratingRevision(true)
    try {
      const revision = await documentService.generateRevision(userDocumentId, {
        template_id: selectedTemplate,
        use_draft: true,
        trigger: 'button',
      })

      // If already ready, download immediately
      let finalStatus: string = revision.status
      if (finalStatus === 'compiling') {
        // Wait for SSE event instead of polling
        finalStatus = await new Promise<string>((resolve) => {
          const timeout = setTimeout(() => resolve('timeout'), 60000)
          const unsubscribe = subscribe((event) => {
            if (event.type !== 'revision_status_changed') return
            const data = event.data as { revision_id: number; status: string }
            if (data.revision_id === revision.id) {
              clearTimeout(timeout)
              unsubscribe()
              resolve(data.status)
            }
          })
        })
      }

      if (finalStatus === 'ready') {
        const result = await documentService.getRevisionDownloadUrl(
          userDocumentId,
          revision.id,
          'pdf',
        )
        // Prefer `window.open` so the browser treats this as a navigation/download flow
        // even when the trigger happens after async work.
        const openedWindow = window.open(result.download_url, '_blank')

        if (!openedWindow) {
          // Fallback to anchor click
          const link = document.createElement('a')
          link.href = result.download_url
          link.download = result.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } else if (finalStatus === 'failed') {
        alert('Failed to generate resume. Please try again.')
      } else if (finalStatus === 'timeout') {
        alert('Resume generation is taking longer than expected. Please try again.')
      }
    } catch (error) {
      console.error('Failed to generate revision from preview:', error)
      alert('Failed to generate resume. Please try again.')
    } finally {
      setIsGeneratingRevision(false)
    }
  }

  const [isDownloadingPreview, setIsDownloadingPreview] = useState(false)

  const handleDownloadFromPreview = async () => {
    if (!userDocumentId) return

    setIsDownloadingPreview(true)
    try {
      const { download_url } = await documentService.getOriginalDownloadUrl(userDocumentId)
      window.open(download_url, '_blank')
    } catch (error) {
      console.error('Failed to download tailored resume:', error)
      alert('Failed to download resume. Please try again.')
    } finally {
      setIsDownloadingPreview(false)
    }
  }

  // Mark learning module as complete
  async function handleMarkModuleComplete() {
    if (!learningContent?.module_id) return
    try {
      await learningService.updateProgress(learningContent.module_id, {
        status: 'completed',
        progress_percent: 100,
      })
      setLearningContent((prev) =>
        prev ? { ...prev, progress: { status: 'completed', progress_percent: 100 } } : prev,
      )
      if (learningContent.topic_id) {
        queryClient.invalidateQueries({
          queryKey: learningKeys.topicDetail(learningContent.topic_id),
        })
        queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
      }
    } catch (error) {
      console.error('Failed to mark module complete:', error)
    }
  }

  const handleContinueNextModule = useCallback(async () => {
    if (!learningContent?.topic_id || !learningContent.module_id) return

    setIsLoadingLearning(true)
    setLearningError(null)
    try {
      const content = await loadNextModuleForTopic(
        learningContent.topic_id,
        learningContent.module_id,
      )
      if (!content?.content) {
        setLearningError('The next module is not ready yet. Please try again shortly.')
        return
      }
      setLearningContent(content)
      queryClient.invalidateQueries({
        queryKey: learningKeys.topicDetail(learningContent.topic_id),
      })
      queryClient.invalidateQueries({ queryKey: learningKeys.modules() })
    } catch (error) {
      console.error('Failed to load next learning module:', error)
      setLearningError('Failed to load the next module. Please try again.')
    } finally {
      setIsLoadingLearning(false)
    }
  }, [learningContent, queryClient, setIsLoadingLearning, setLearningContent, setLearningError])

  // Determine center content
  const renderCenterContent = () => {
    if (showLearningPanel) {
      return (
        <LearningPanel
          content={learningContent}
          isLoading={isLoadingLearning}
          error={learningError}
          topicGenerating={learningTopicGenerating}
          onClose={() => {
            setLearningPanelOpen(false)
            setIsLoadingLearning(false)
            setLearningError(null)
            setLearningTopicGenerating(null)
          }}
          onMarkComplete={handleMarkModuleComplete}
          onContinueNext={handleContinueNextModule}
          isLoadingNext={isLoadingLearning}
        />
      )
    }
    if (showPreviewPanel) {
      return (
        <ResumePreviewPanel
          content={resumeContent}
          originalContent={originalResumeContent}
          template={selectedTemplate}
          onTemplateChange={setSelectedTemplate}
          onGenerate={handleGenerateFromPreview}
          onDownload={previewIsAgentTailored ? handleDownloadFromPreview : undefined}
          onClose={() => {
            setPreviewPanelOpen(false)
            clearResumePreviewContext()
          }}
          isGenerating={isGeneratingRevision}
          isUpdatingContent={isUpdatingPreview}
          isLoading={previewLoading}
          isAgentTailored={previewIsAgentTailored}
          isDownloading={isDownloadingPreview}
          pdfUrl={previewPdfUrl}
          jobTitle={previewJobTitle}
          company={previewCompany}
        />
      )
    }
    return null
  }

  const sidePanelContent = renderCenterContent()

  const pelgoLogoLink = (opts?: {
    compact?: boolean
    sidebar?: boolean
    onAfterNavigate?: () => void
  }) => (
    <Link to="/home" className="flex items-center" onClick={() => opts?.onAfterNavigate?.()}>
      <div
        className={cn(
          'overflow-hidden bg-transparent shrink-0',
          opts?.compact ? 'h-6 w-8' : 'h-5 w-auto max-w-[100px]',
        )}
      >
        {!logoLoadFailed ? (
          <img
            src={
              opts?.sidebar
                ? opts.compact
                  ? '/logomark-white.svg'
                  : '/logo-white.png'
                : '/logomark-white.png'
            }
            alt="Pelgo"
            className={cn(
              'h-full object-contain object-left',
              opts?.compact ? 'w-auto max-w-none' : 'w-full',
            )}
            onError={() => setLogoLoadFailed(true)}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-white font-bold text-sm">
            P
          </div>
        )}
      </div>
    </Link>
  )

  const notifySidebarNavigation = useCallback(() => {
    if (typeof window === 'undefined') return
    // A route selected during the priority menu tour invalidates the page flow
    // that was deferred from the previous route. The destination can enqueue
    // its own flow after it mounts.
    useFtueStore.getState().clearDeferredFlow()
    window.dispatchEvent(new CustomEvent('pelgo:sidebar-navigation'))
  }, [])

  const sidebarNav = (anchorRefs: typeof welcomeBackMenuFtue.desktop) => (
    <nav
      ref={anchorRefs.overviewAnchorRef}
      className="flex flex-col gap-0.5 px-3 flex-1 pt-2 md:pt-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
    >
      <div className="flex flex-col gap-0.5 pl-1 md:pl-2 flex-1 min-h-0 overflow-y-auto">
        {visibleCoachChildren.map((item, index) => {
          const Icon = item.icon
          const showSection =
            !!item.section &&
            (index === 0 || visibleCoachChildren[index - 1]?.section !== item.section)
          const isActive = item.matchPaths.some(
            (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
          )
          let resumeProgress: { progress: number; label: string } | null = null
          if (item.badgeKey === 'resume') {
            if (processingItems.length > 0) {
              resumeProgress = {
                progress: processingItems[0]?.progress ?? 0,
                label: processingItems[0]?.statusText || 'Processing...',
              }
            } else if (isUploadInProgress) {
              resumeProgress = {
                progress: uploadProgress,
                label: uploadStatusText || 'Uploading...',
              }
            }
          }

          if (item.children) {
            const visibleChildren = item.children.filter((child) => !child.hidden)
            if (visibleChildren.length === 0) return null

            return (
              <div
                key={item.name}
                aria-label={item.name}
                ref={
                  visibleChildren.length > 1 && item.ftue
                    ? anchorRefs.itemAnchorRef(item.ftue.id)
                    : undefined
                }
              >
                {showSection && (
                  <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-white/80 first:pt-1">
                    {item.section}
                  </div>
                )}
                <Link
                  to={item.href}
                  onClick={notifySidebarNavigation}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-card text-brand-700 shadow-sm'
                      : 'text-white hover:bg-card/15 hover:text-white',
                  )}
                >
                  <Icon className={cn('h-4 w-4', isActive && 'text-brand-600')} />
                  <span>{item.name}</span>
                </Link>
                <div className="mb-1 ml-6 flex flex-col gap-0.5 border-l border-white/30 pl-3">
                  {visibleChildren.map((child) => {
                    const childIsActive = child.matchPaths.some(
                      (path) =>
                        location.pathname === path || location.pathname.startsWith(`${path}/`),
                    )

                    return (
                      <Link
                        key={child.href}
                        ref={child.ftue ? anchorRefs.itemAnchorRef(child.ftue.id) : undefined}
                        to={child.href}
                        onClick={notifySidebarNavigation}
                        className={cn(
                          'rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors',
                          childIsActive
                            ? 'bg-brand-50 text-brand-700 dark:bg-blue-950/40 dark:text-brand-200'
                            : 'text-white hover:bg-card/15 hover:text-white',
                        )}
                      >
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )
          }

          return (
            <div key={item.name}>
              {showSection && (
                <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-white/80 first:pt-1">
                  {item.section}
                </div>
              )}
              <Link
                ref={item.ftue ? anchorRefs.itemAnchorRef(item.ftue.id) : undefined}
                to={item.href}
                onClick={notifySidebarNavigation}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-card text-brand-700 shadow-sm'
                    : 'text-white hover:bg-card/15 hover:text-white',
                )}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-brand-600')} />
                <span className="flex-1">{item.name}</span>
              </Link>

              {item.href === '/opportunities' && hasOpportunitySubsections && (
                <div className="mt-1 pl-6 space-y-1">
                  <Link
                    to="/opportunities"
                    onClick={notifySidebarNavigation}
                    className={cn(
                      'block px-2 py-1 text-[13px] rounded-md transition-colors',
                      isOpportunitiesRoute &&
                        resumeIdFromUrl === null &&
                        jobSearchIdFromUrl === null
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-white hover:bg-card/15 hover:text-white',
                    )}
                  >
                    Launch job search agent
                  </Link>
                  {customSearchSets.length > 0 && (
                    <div className="pt-1">
                      <div className="px-2 pb-1 text-[11px] uppercase tracking-wide text-white/80">
                        Job Search Agents
                      </div>
                      {(() => {
                        const processingSearches = customSearchSets
                          .filter(
                            (search) => search.status === 'pending' || search.status === 'matching',
                          )
                          .slice(0, 8)
                        const readySearches = customSearchSets
                          .filter(
                            (search) => search.status !== 'pending' && search.status !== 'matching',
                          )
                          .slice(0, 8)
                        return (
                          <>
                            {processingSearches.length > 0 && (
                              <div className="space-y-2">
                                {processingSearches.map((search) => {
                                  const isActiveGenerated =
                                    isOpportunitiesRoute && jobSearchIdFromUrl === search.id
                                  const total = search.total_jobs ?? null
                                  const completed = search.completed_jobs ?? null
                                  const computedProgress =
                                    total && total > 0 && completed !== null
                                      ? Math.max(
                                          0,
                                          Math.min(100, Math.round((completed / total) * 100)),
                                        )
                                      : null
                                  const progress = computedProgress ?? 0
                                  return (
                                    <div key={search.id} className="relative">
                                      <Link
                                        to={`/opportunities?job_search_id=${search.id}`}
                                        onClick={notifySidebarNavigation}
                                        className={cn(
                                          'block rounded-md px-2 py-1 pr-8 transition-colors',
                                          isActiveGenerated
                                            ? 'bg-brand-50 text-brand-700'
                                            : 'text-white hover:bg-card/15 hover:text-white',
                                        )}
                                        title="Matched jobs are still being generated"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-white" />
                                          <div className="min-w-0 flex-1">
                                            <div className="truncate text-[12px] font-medium text-white">
                                              {search.job_title || `Search ${search.id}`}
                                            </div>
                                            <div className="text-[11px] text-white">Working...</div>
                                          </div>
                                          <div className="text-[12px] font-semibold tabular-nums text-white">
                                            {computedProgress !== null ? `${progress}%` : '...'}
                                          </div>
                                        </div>
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                          void handleDeleteJobSearch(search.id, search.job_title)
                                        }}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-white hover:bg-card/15 hover:text-white"
                                        aria-label={`Delete ${search.job_title || `Search ${search.id}`}`}
                                        title="Delete search"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                            {readySearches.length > 0 && (
                              <div className="space-y-1">
                                {readySearches.map((search) => {
                                  const isActiveGenerated =
                                    isOpportunitiesRoute && jobSearchIdFromUrl === search.id
                                  const statusLabel =
                                    search.match_count != null ? `${search.match_count}` : 'Done'
                                  return (
                                    <div key={search.id} className="relative">
                                      <Link
                                        to={`/opportunities?job_search_id=${search.id}`}
                                        onClick={notifySidebarNavigation}
                                        className={cn(
                                          'block rounded-md transition-colors',
                                          isActiveGenerated
                                            ? 'bg-brand-50 text-brand-700'
                                            : 'text-white hover:bg-card/15 hover:text-white',
                                        )}
                                      >
                                        <div className="flex items-center justify-between gap-2 px-2 py-1 pr-8">
                                          <span className="truncate text-[12px] font-medium">
                                            {search.job_title || `Search ${search.id}`}
                                          </span>
                                          <span className="text-[10px] text-white tabular-nums">
                                            {statusLabel}
                                          </span>
                                        </div>
                                      </Link>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.preventDefault()
                                          event.stopPropagation()
                                          void handleDeleteJobSearch(search.id, search.job_title)
                                        }}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-white hover:bg-card/15 hover:text-white"
                                        aria-label={`Delete ${search.job_title || `Search ${search.id}`}`}
                                        title="Delete search"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )}
                  {(generatingMatchResumeIds.length > 0 ||
                    readyMatchedResumeIds.length > 0 ||
                    failedMatchResumeIds.length > 0) && (
                    <div className="pt-1">
                      <div className="px-2 pb-1 text-[11px] uppercase tracking-wide text-white/80">
                        Resume Scan Agents
                      </div>
                      {generatingMatchResumeIds.length > 0 && (
                        <div className="space-y-2">
                          {generatingMatchResumeIds.map((resumeId) => {
                            const resume = resumeById[resumeId]
                            const total = resume?.job_matching_total ?? null
                            const completed = resume?.job_matching_completed_count ?? null
                            const computedProgress =
                              total && total > 0 && completed !== null
                                ? Math.max(0, Math.min(100, Math.round((completed / total) * 100)))
                                : null
                            const progress = computedProgress ?? 0
                            return (
                              <Link
                                key={resumeId}
                                // FTUE Screen 3 handoff anchor (PEL-519): the live
                                // scan item for this resume. Resume route + desktop
                                // only; off-page/mobile the anchor is absent so the
                                // engine resolves the anchorless fallback. The ready
                                // item below carries the same anchor so the coachmark
                                // survives the generating -> done transition.
                                ref={isResumeRoute && !isMobile ? scanAgentsAnchorRef : undefined}
                                to={`/opportunities?resume_id=${resumeId}`}
                                onClick={() => {
                                  notifySidebarNavigation()
                                  markMatchedResumeViewed(resumeId)
                                }}
                                className={cn(
                                  'block rounded-md px-2 py-1 transition-colors',
                                  isOpportunitiesRoute && resumeIdFromUrl === resumeId
                                    ? 'bg-brand-50 text-brand-700'
                                    : 'text-white hover:bg-card/15 hover:text-white',
                                )}
                                title="Matched jobs are still being generated"
                              >
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-5 w-5 shrink-0 animate-spin text-white" />
                                  <div className="min-w-0 flex-1">
                                    <div className="truncate text-[12px] font-normal text-white">
                                      {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                                    </div>
                                    <div className="text-[11px] text-white">Working...</div>
                                  </div>
                                  <div className="text-[12px] font-semibold tabular-nums text-white">
                                    {computedProgress !== null ? `${progress}%` : '...'}
                                  </div>
                                </div>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                      {failedMatchResumeIds.length > 0 && (
                        <div className="space-y-1">
                          {failedMatchResumeIds.map((resumeId) => (
                            <Link
                              key={resumeId}
                              to="/resume"
                              onClick={notifySidebarNavigation}
                              className={cn(
                                'block rounded-md px-2 py-1 transition-colors',
                                'text-white hover:bg-card/15 hover:text-white',
                              )}
                              title="Resume scan did not complete. Retry from the resume page."
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-[12px] font-normal text-white">
                                    {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                                  </div>
                                  <div className="text-[11px] text-white">Failed</div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {readyMatchedResumeIds.map((resumeId) =>
                    (() => {
                      const shouldBlink = readyToBlinkResumeIds.includes(resumeId)
                      const isViewed = viewedReadyMatchedResumeIds.includes(resumeId)
                      const resumeDoc = resumeById[resumeId]
                      const matchesForResume = matchedJobsByResume[resumeId]
                      const sidebarCount = sidebarMatchedJobCount(resumeDoc, matchesForResume)

                      const content = isViewed ? (
                        <div className="flex items-center justify-between gap-2 px-2 py-1">
                          <span className="truncate text-[12px] font-normal">
                            {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                          </span>
                          <span className="text-[10px] text-white tabular-nums">
                            {sidebarCount}
                          </span>
                        </div>
                      ) : (
                        <div
                          className={cn(
                            'flex items-center gap-2 px-2 py-1',
                            shouldBlink && 'animate-pulse',
                          )}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[12px] font-normal text-status-700">
                              {resumeTitleById[resumeId] || `Resume ${resumeId}`}
                            </div>
                            <div className="text-[11px] text-white">Done</div>
                          </div>
                          <span className="text-[12px] font-semibold tabular-nums text-white">
                            100%
                          </span>
                        </div>
                      )

                      return (
                        <Link
                          key={resumeId}
                          // Same FTUE handoff anchor as the generating item, so the
                          // Screen 3 coachmark stays put when the scan finishes while
                          // the user is still looking at it (PEL-519).
                          ref={isResumeRoute && !isMobile ? scanAgentsAnchorRef : undefined}
                          to={`/opportunities?resume_id=${resumeId}`}
                          onClick={() => {
                            notifySidebarNavigation()
                            markMatchedResumeViewed(resumeId)
                          }}
                          className={cn(
                            'block rounded-md transition-colors',
                            resumeIdFromUrl === resumeId
                              ? 'bg-brand-50 text-brand-700'
                              : 'text-white hover:bg-card/15 hover:text-white',
                          )}
                        >
                          {content}
                        </Link>
                      )
                    })(),
                  )}
                </div>
              )}

              {resumeProgress && (
                <div className="px-3 mt-1 mb-1">
                  <p className="text-[10px] text-white mb-1 truncate">{resumeProgress.label}</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-card/25 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-card rounded-full transition-all duration-500"
                        style={{ width: `${resumeProgress.progress}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-white tabular-nums">
                      {Math.round(resumeProgress.progress)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </nav>
  )

  const compactSidebarNav = (
    <nav
      aria-label="Navigation"
      className="flex flex-1 flex-col items-center gap-0.5 px-2 py-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
    >
      {/* Same source as the expanded sidebar: the raw list is in declaration
          order, so collapsing used to reshuffle destinations (Benefits jumped
          to the end). */}
      {visibleCoachChildren
        .filter((item) => !(PROFILE_IN_USER_MENU && item.href === '/profile'))
        .map((item) => {
          const visibleChildren = item.children?.filter((child) => !child.hidden) ?? []
          if (item.children && visibleChildren.length === 0) return null

          const Icon = item.icon
          const isActive = item.matchPaths.some(
            (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
          )

          return (
            <div
              key={item.name}
              className="group relative shrink-0"
              onMouseLeave={() => {
                if (dismissedCompactSubmenu === item.name) setDismissedCompactSubmenu(null)
              }}
            >
              <Link
                ref={
                  item.ftue ? welcomeBackMenuFtue.desktop.itemAnchorRef(item.ftue.id) : undefined
                }
                to={item.href}
                onClick={(event) => {
                  notifySidebarNavigation()
                  if (visibleChildren.length > 0) setDismissedCompactSubmenu(item.name)
                  event.currentTarget.blur()
                }}
                aria-label={item.name}
                aria-haspopup={visibleChildren.length > 0 ? 'menu' : undefined}
                title={visibleChildren.length === 0 ? item.name : undefined}
                onFocus={() => setDismissedCompactSubmenu(null)}
                className={cn(
                  'relative flex h-9 w-10 items-center justify-center rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
                  isActive
                    ? 'bg-card text-brand-700 shadow-sm'
                    : 'text-white hover:bg-card/15 hover:text-white',
                )}
              >
                <Icon className={cn('h-4 w-4', isActive && 'text-brand-600')} aria-hidden="true" />
              </Link>

              {visibleChildren.length > 0 ? (
                <div
                  role="menu"
                  aria-label={`${item.name} submenu`}
                  className={cn(
                    'pointer-events-none invisible absolute left-full top-0 z-50 ml-3 min-w-48 translate-x-1 rounded-lg border bg-background p-1.5 opacity-0 shadow-xl transition-[opacity,transform,visibility] duration-150 ease-out before:absolute before:-left-3 before:top-0 before:h-full before:w-3 motion-reduce:transition-none',
                    dismissedCompactSubmenu !== item.name &&
                      'group-hover:pointer-events-auto group-hover:visible group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:translate-x-0 group-focus-within:opacity-100',
                  )}
                >
                  <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
                    {item.name}
                  </div>
                  {visibleChildren.map((child) => {
                    const childIsActive = child.matchPaths.some(
                      (path) =>
                        location.pathname === path || location.pathname.startsWith(`${path}/`),
                    )
                    const ChildIcon = child.icon

                    return (
                      <Link
                        key={child.href}
                        role="menuitem"
                        to={child.href}
                        onClick={(event) => {
                          notifySidebarNavigation()
                          setDismissedCompactSubmenu(item.name)
                          event.currentTarget.blur()
                        }}
                        className={cn(
                          'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors',
                          childIsActive
                            ? 'bg-brand-50 text-brand-700 dark:bg-blue-950/40 dark:text-brand-200'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <ChildIcon className="h-4 w-4" aria-hidden="true" />
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              ) : (
                <span
                  role="tooltip"
                  className="pointer-events-none invisible absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-[opacity,transform,visibility] duration-150 ease-out group-hover:visible group-hover:translate-x-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-x-0 group-focus-within:opacity-100 motion-reduce:transition-none"
                >
                  {item.name}
                </span>
              )}
            </div>
          )
        })}
    </nav>
  )

  const legalLinks = (
    <div className="px-4 py-3">
      <div className="flex items-center justify-center text-[10px] text-white/90">
        <a
          href="https://pelgo.com/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-white hover:underline transition-colors"
        >
          Terms of Use
        </a>
        <span className="mx-2 select-none text-white/60" aria-hidden="true">
          •
        </span>
        <a
          href="https://pelgo.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-white hover:underline transition-colors"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  )

  const userMenuDropdown = (placement: 'sidebar' | 'compact') => (
    <div data-user-menu className="relative">
      <button
        type="button"
        onClick={() => setShowUserMenu((prev) => !prev)}
        className={cn(
          'flex items-center gap-2.5 rounded-lg transition-colors',
          placement === 'sidebar'
            ? 'w-full px-2 py-1.5 text-white hover:bg-card/15'
            : 'mx-auto h-10 w-10 justify-center p-1 text-white hover:bg-card/15',
        )}
        aria-label={placement === 'compact' ? 'Open profile menu' : undefined}
      >
        <div
          className={cn(
            'w-8 h-8 rounded-full border flex items-center justify-center text-xs font-medium shrink-0',
            'bg-card/20 border-white/40 text-white',
          )}
        >
          {displayInitial}
        </div>
        {placement === 'sidebar' && (
          <>
            <span className="min-w-0 flex-1 truncate text-left text-[11px] font-medium leading-none tracking-tight text-white">
              {displayName}
            </span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 text-white transition-transform',
                showUserMenu && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
      {showUserMenu && (
        <div
          className={cn(
            'absolute rounded-lg border bg-background shadow-lg py-1 z-50',
            placement === 'sidebar'
              ? 'bottom-full left-3 right-3 mb-1'
              : 'bottom-0 left-full ml-2 min-w-[190px]',
          )}
        >
          {PROFILE_IN_USER_MENU && canUseParticipantRoute('participant.profile') && (
            <button
              type="button"
              onClick={() => {
                setShowUserMenu(false)
                navigate('/profile')
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <User className="h-4 w-4" />
              Profile
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              setShowUserMenu(false)
              try {
                await logout()
                navigate('/login', { replace: true })
              } catch (error) {
                console.error('Logout failed:', error)
              }
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 hover:bg-muted transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  )

  const sidebarContentMobile = (
    <>
      <div className="px-5 py-4 flex items-center justify-between">
        {pelgoLogoLink({ onAfterNavigate: () => setMobileMenuOpen(false) })}
        <button
          type="button"
          className="p-1 rounded-lg hover:bg-card/15"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {sidebarNav(welcomeBackMenuFtue.mobile)}
      {legalLinks}
      <div className="px-3 py-2 relative mt-auto">{userMenuDropdown('sidebar')}</div>
    </>
  )

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-canvas">
      <div
        className="relative flex flex-1 min-h-0 min-w-0 bg-canvas md:gap-3 md:p-3"
        style={
          {
            '--coach-panel-width': `${chatWidth}px`,
          } as CSSProperties
        }
      >
        {/* Mobile top bar */}
        <div
          className={cn(
            'md:hidden fixed top-0 left-0 right-0 z-30 h-12 border-b bg-background flex items-center justify-between px-3',
            hideNav && 'hidden',
          )}
        >
          <button
            ref={welcomeBackMenuFtue.mobile.openMobileAnchorRef}
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="p-1.5 rounded-lg hover:bg-muted"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="absolute left-1/2 -translate-x-1/2">
            {pelgoLogoLink({ compact: true })}
          </div>
          {hideCoachPanel ? (
            <div className="w-8 shrink-0" aria-hidden />
          ) : (
            <button
              type="button"
              onClick={() => {
                clearCoachAttention()
                setMobileChatOpen(true)
              }}
              className="relative p-1.5 rounded-lg hover:bg-muted shrink-0"
              aria-label="Open coach"
            >
              <MessageCircle className="h-5 w-5" />
              {coachAttentionActive && !mobileChatOpen && (
                <span className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-brand-500 animate-pulse" />
              )}
            </button>
          )}
        </div>

        {/* Mobile sidebar overlay */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile sidebar drawer */}
        <aside
          className={cn(
            'sidebar-gradient text-white md:hidden fixed inset-y-0 left-0 z-50 w-64 flex flex-col transition-transform duration-200',
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
          onClickCapture={notifySidebarNavigation}
        >
          <div className="flex flex-col flex-1 min-h-0">{sidebarContentMobile}</div>
        </aside>

        {/* Mobile chat overlay */}
        {mobileChatOpen && !hideCoachPanel && (
          <div className="md:hidden fixed inset-0 z-40 bg-background flex flex-col">
            <div
              className={cn(
                'h-12 border-b flex items-center justify-between px-4 shrink-0',
                coachAttentionActive && 'animate-pulse bg-status-50 border-status-200',
              )}
            >
              <span
                className={cn(
                  'text-sm font-semibold flex items-center gap-2',
                  coachAttentionActive && 'text-status-700',
                )}
              >
                Pelgo coach
                {coachAttentionActive && (
                  <span className="h-2 w-2 rounded-full bg-status-500 animate-pulse" aria-hidden />
                )}
              </span>
              <button
                type="button"
                onClick={() => setMobileChatOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <ChatPanel />
            </div>
          </div>
        )}

        {/* Desktop shell — nav and coach float as panels; the center column is the
            canvas itself, so page content floats directly on the background. */}
        <div
          className={cn(
            'hidden md:flex flex-1 min-w-0 min-h-0 gap-3',
            'transition-all duration-[1500ms] ease-out',
            hasMounted ? 'translate-x-0 opacity-100' : '-translate-x-6 opacity-0',
          )}
        >
          <aside
            aria-label="Primary navigation"
            className={cn(
              'sidebar-gradient text-white shadow-sm flex-col shrink-0 min-h-0 rounded-2xl transition-[width] duration-300 ease-out motion-reduce:transition-none',
              hideNav ? 'hidden' : 'flex',
              compactSidebar ? 'w-16 overflow-visible' : 'w-56 overflow-hidden',
            )}
            onClickCapture={notifySidebarNavigation}
          >
            <div
              className={cn(
                'flex h-16 shrink-0 items-center',
                compactSidebar ? 'justify-center px-2' : 'px-5',
              )}
            >
              {pelgoLogoLink({ compact: compactSidebar, sidebar: true })}
            </div>
            {compactSidebar ? compactSidebarNav : sidebarNav(welcomeBackMenuFtue.desktop)}
            <div className={cn('relative mt-auto', compactSidebar ? 'px-2 py-3' : 'px-3 py-2')}>
              {userMenuDropdown(compactSidebar ? 'compact' : 'sidebar')}
            </div>
            {!compactSidebar && legalLinks}
          </aside>

          <div className="flex flex-1 min-w-0 min-h-0 gap-3">
            {/* Center content — transparent; each page element floats on the canvas */}
            <main className="flex-1 min-w-0 overflow-hidden relative bg-transparent">
              {shouldRedirectFromLockedRoute ? (
                <Navigate to="/resume" replace />
              ) : isResumeStatusPendingOnLockedRoute ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-status" />
                </div>
              ) : (
                <>
                  {/* Regular page content - use hidden instead of unmount to preserve state */}
                  <div
                    key={location.pathname}
                    className={cn('h-full animate-page-in', sidePanelContent ? 'hidden' : '')}
                  >
                    <Outlet />
                  </div>
                  {/* Side panel content overlays center */}
                  {sidePanelContent && <div className="h-full">{sidePanelContent}</div>}
                </>
              )}
            </main>

            {/* Right chat panel with resize handle (desktop only) */}
            {!hideCoachPanel && (
              <div
                className={cn(
                  'hidden md:flex shrink-0 min-h-0 relative overflow-hidden bg-background border border-border rounded-2xl shadow-sm',
                  'transition-all duration-1000 ease-out delay-150',
                  hasMounted ? 'translate-x-0 opacity-100' : 'translate-x-6 opacity-0',
                  coachAttentionActive && 'ring-2 ring-status-500/60 border-status-300',
                )}
                style={{ width: chatWidth }}
              >
                {/* Absolute so the panel's surface reaches its own rounded
                    edge; a handle in flow left a visible notch at the corner. */}
                <div
                  className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize hover:bg-brand-500/50 active:bg-brand-500/70 transition-colors"
                  onMouseDown={handleResizeStart}
                  title="Drag to resize"
                />
                <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                  <ChatPanel />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile main content */}
        <main className={cn('flex-1 overflow-hidden relative md:hidden', !hideNav && 'pt-12')}>
          {shouldRedirectFromLockedRoute ? (
            <Navigate to="/resume" replace />
          ) : isResumeStatusPendingOnLockedRoute ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-status" />
            </div>
          ) : (
            <>
              <div className={cn('h-full', sidePanelContent ? 'hidden' : '')}>
                <Outlet />
              </div>
              {sidePanelContent && <div className="h-full">{sidePanelContent}</div>}
            </>
          )}
        </main>
      </div>

      {/* FTUE renderer — mounted once, portals into document.body (PEL-516). */}
      <FtueHost />

      {/* Repeatable anchored notices — mounted once, alongside the FTUE host. */}
      <AppNoticeCoachmark />
    </div>
  )
}
